import { apiSlice } from '../api/apiSlice'
import { EmployeeStatus, Project, RequestProps, User, UserModifyDTO, UsersResponse } from '../../types'
import RoleType from '../../types/enums/RoleType'
import { changeEmptyStringsToNull, changeNullsToEmptyString } from '../../utils/falsyValuesConverter'
import Settings, { defaultEmployeeSettings } from '../../types/models/Settings'
import NotificationType from '../../types/enums/NotificationType'
import { NotificationReason } from '../../types/enums/NotificationReason'

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getCurrentUser: builder.query({
      query: () => `/employees/logged`,
      transformResponse: (responseData: any): UserWithSettings => {
        const { settings, ...rest } = responseData

        const employeeSettings = settings ?? defaultEmployeeSettings

        const defaultProject = settings?.defaultProject
          ? {
              label: settings.defaultProject.name,
              value: settings.defaultProject.id,
            }
          : null

        return {
          user: rest as User,
          settings: {
            ...employeeSettings,
            defaultProject,
          } as Settings,
        }
      },
      providesTags: (result, _error) => [
        { type: 'User', id: 'LIST' },
        { type: 'User', id: `settings-${result?.user.id}` },
      ],
    }),

    getUsers: builder.query<UsersResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'employees',
        params: props,
      }),
      transformResponse: (responseData: UsersResponse) => {
        return responseData
      },
      providesTags: (_result, _error, props) => {
        const leaderId = props['projects.leaders.leader.id']
        return leaderId
          ? [
              { type: 'User', id: `LEADER-${leaderId}` },
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }]
      },
    }),

    getLightUsers: builder.query<UsersResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'employees/light',
        params: props,
      }),
      transformResponse: (responseData: UsersResponse) => {
        return responseData
      },
      providesTags: () => [{ type: 'User', id: 'LIST' }],
    }),
    getLightProjectLeaders: builder.query<UsersResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'employees/light',
        params: {
          ...props,
          'roles.name': RoleType.ProjectLeader,
        },
      }),
      transformResponse: (responseData: UsersResponse) => {
        return responseData
      },
      providesTags: () => [{ type: 'User', id: 'LIST' }],
    }),
    getLightLeaders: builder.query<UsersResponse, RequestProps>({
      query: (props: RequestProps) => ({
        url: 'employees/light',
        params: {
          ...props,
          'roles.name': 'LEADER',
          'roles.name!': 'PROJECT_LEADER',
        },
      }),
      transformResponse: (responseData: UsersResponse) => {
        return responseData
      },
      providesTags: () => [{ type: 'User', id: 'LIST' }],
    }),
    getBlockedUsersWithWorkHours: builder.query<User[], { yearMonth: string }>({
      query: ({ yearMonth }) => ({
        url: 'employees/light/with-work-hours',
        params: {
          employeeStatus: 'BLOCKED',
          yearMonth,
        },
      }),
      providesTags: () => [{ type: 'User', id: 'LIST' }],
    }),
    getUserById: builder.query<User, string>({
      query: (id: string) => `/employees/${id}`,
      transformResponse: (responseData: User) => {
        return { ...changeNullsToEmptyString(responseData), latestContract: responseData.latestContract }
      },
      providesTags: () => [{ type: 'User', id: 'LIST' }],
    }),

    createUser: builder.mutation({
      query: (user: Partial<UserModifyDTO>) => ({
        url: '/employees',
        method: 'POST',
        body: changeEmptyStringsToNull(user),
      }),
      invalidatesTags: () => [{ type: 'User', id: 'LIST' }],
    }),

    editUser: builder.mutation<User, EditArgs>({
      query: ({ id, user, leadersEmployees, newLeaderId }) => {
        let editedUser = { ...user, leaders: user?.leaders }
        let url = `/employees/${id}`
        if (leadersEmployees?.length !== undefined && leadersEmployees?.length > 0) {
          let concatenatedString = leadersEmployees.join(',')
          url = url + `?employeeIds=${concatenatedString}&newLeaderId=${newLeaderId}`
        }
        return {
          url: url,
          method: 'PUT',
          body: changeEmptyStringsToNull(editedUser),
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id: 'LIST' },
        { type: 'User', id: id },
      ],
    }),

    changeEmployeeStatus: builder.mutation<User, ChangeStatusArgs>({
      query: ({ id, employeeStatus }) => {
        return {
          url: `/employees/change-status/${id}`,
          method: 'PUT',
          body: JSON.stringify(employeeStatus),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      },
      invalidatesTags: () => [{ type: 'User', id: 'LIST' }],
    }),

    deleteUser: builder.mutation<User, string>({
      query: (id: string) => ({
        url: `/employees/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'User', id: 'LIST' }],
    }),

    getLeadershipData: builder.query<LeadershipData, string>({
      query: (id: string) => `/employees/${id}/leadership-data`,
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),

    changeSettings: builder.mutation<User, SettingsArgs>({
      query: ({ id, ...body }) => {
        return {
          url: `/employees/change-settings/${id}`,
          method: 'PUT',
          body: body,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id: `settings-${id}` }],
    }),
  }),
})

type EditArgs = {
  id: string
  user: Partial<UserModifyDTO>
  leadersEmployees?: string[]
  newLeaderId?: string
}

type ChangeStatusArgs = {
  id: string
  employeeStatus: EmployeeStatus
}

type SettingsArgs = {
  id: string
  start: string
  end: string
  hideSchedule: boolean
  remoteWorkMode: boolean
  defaultProjectId: string | undefined
  workHourCollapseThreshold: number
  workHoursSortByFavorite: boolean
  notificationType: NotificationType
  mutedNotificationReasons: NotificationReason[]
}

type UserWithSettings = {
  user: User
  settings: Settings
}

export type LeadershipData = {
  employees: User[]
  projects: Project[]
}

// Generated hooks
export const {
  useGetCurrentUserQuery,
  useGetUsersQuery,
  useGetLightUsersQuery,
  useLazyGetUsersQuery,
  useLazyGetLightLeadersQuery,
  useGetLightLeadersQuery,
  useGetUserByIdQuery,
  useGetLightProjectLeadersQuery,
  useLazyGetLightProjectLeadersQuery,
  useLazyGetUserByIdQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useChangeEmployeeStatusMutation,
  useDeleteUserMutation,
  useChangeSettingsMutation,
  useLazyGetLeadershipDataQuery,
  useGetBlockedUsersWithWorkHoursQuery,
} = extendedApiSlice
